import type { ComponentProps, ReactNode } from "react";
import { Card } from "@carpybara/ui/shadcn/card";
import { cn } from "@carpybara/ui/shadcn/utils";

/**
 * Shared auth-form composition used by login and registration. These are
 * application-level patterns rather than generic design-system controls:
 * Input and Button still come from @carpybara/ui, while this module owns the
 * Carpybara auth layout around them.
 */
export function AuthCard({ className, ...props }: ComponentProps<"div">) {
  return <Card className={cn("space-y-6 p-6 sm:p-8", className)} {...props} />;
}

export function AuthCardHeader({
  title,
  description,
}: {
  title: ReactNode;
  description: ReactNode;
}) {
  return (
    <header className="space-y-1.5 text-center">
      <h1 className="text-2xl font-bold tracking-tight text-(--text-primary)">
        {title}
      </h1>
      <div className="text-sm text-(--text-muted)">{description}</div>
    </header>
  );
}

export function AuthInputField({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <div className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-(--text-muted)">
        {icon}
      </div>
      <div className="[&>*]:ps-9">{children}</div>
    </div>
  );
}

export function AuthDivider({ label }: { label: string }) {
  return (
    <div className="relative flex items-center gap-3">
      <div className="h-px flex-1 bg-(--border)" />
      <span className="text-[11px] uppercase tracking-wide text-(--text-muted)">
        {label}
      </span>
      <div className="h-px flex-1 bg-(--border)" />
    </div>
  );
}

export function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}
